import { Component, ErrorInfo, ReactNode } from 'react';

/**
 * App-wide error boundary. Catches render/runtime errors anywhere in the tree
 * so users see a recoverable screen instead of a blank white page. Styled with
 * inline styles so it renders even if the stylesheet failed to load. The
 * data-testid is asserted-ABSENT by the Playwright smoke suite, so an error
 * boundary can never silently hide a crash on a healthy route.
 */
interface Props { children: ReactNode }
interface State { hasError: boolean; error?: Error }

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Observability hook — send to a monitoring service in a real deployment.
    console.error('[error-boundary]', error, info?.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        role="alert"
        data-testid="error-boundary"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1.5rem',
          textAlign: 'center',
          fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
          background: '#f8fafc',
          color: '#0f172a',
        }}
      >
        <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }} aria-hidden>⚠️</div>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>Something went wrong</h1>
        <p style={{ marginTop: '0.5rem', maxWidth: 420, color: '#475569' }}>
          An unexpected error occurred. You can try again, or return to the home page.
          If it keeps happening, please let us know via the Feedback page.
        </p>
        <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={() => window.location.reload()}
            style={{ background: '#2563eb', color: '#fff', border: 0, borderRadius: 8, padding: '0.625rem 1.25rem', fontWeight: 600, cursor: 'pointer' }}
          >
            Reload
          </button>
          <a
            href="/"
            style={{ border: '1px solid #cbd5e1', color: '#334155', borderRadius: 8, padding: '0.625rem 1.25rem', fontWeight: 600, textDecoration: 'none' }}
          >
            Go home
          </a>
        </div>
      </div>
    );
  }
}
